import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { MaintenanceLedger, MaintenanceLedgerForm, MaintenanceLedgerSearchForm } from 'entities/maintenance_ledger'
import { MaintenanceLedgerItemBulkForm } from 'entities/maintenance_ledger_item'

type MaintenanceLedgersResponse = PagingResponse & {
    results: MaintenanceLedger[]
}

export function useFetchMaintenanceLedgersApi(
    searchForm?: Form<MaintenanceLedgerSearchForm>
): IndexApiSet<MaintenanceLedgersResponse> & { execute: () => void } {
    const apiPath = 'maintenance_ledgers/'
    const api = useIndexApi<MaintenanceLedgersResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvMaintenanceLedgersApi(searchForm: Form<MaintenanceLedgerSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'maintenance_ledgers/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'maintenance_ledger', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostMaintenanceLedgerApi(): ApiSet<BaseResponse> & { execute: (form: Form<MaintenanceLedgerForm>) => void } {
    const apiSet = usePostApi<BaseResponse, MaintenanceLedgerForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<MaintenanceLedgerForm>) => {
        const apiPath = `maintenance_ledgers/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutMaintenanceLedgerApi(): ApiSet<BaseResponse> & { execute: (form: Form<MaintenanceLedgerForm>) => void } {
    const apiSet = usePutApi<BaseResponse, MaintenanceLedgerForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<MaintenanceLedgerForm>) => {
        const apiPath = `maintenance_ledgers/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function usePostMaintenanceLedgerItemsApi(): ApiSet<BaseResponse> & { execute: (id: number, form: Form<MaintenanceLedgerItemBulkForm>) => void } {
    const apiSet = usePostApi<BaseResponse, MaintenanceLedgerItemBulkForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (id: number, form: Form<MaintenanceLedgerItemBulkForm>) => {
        const apiPath = `maintenance_ledgers/${id}/create_items/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteMaintenanceLedgerApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `maintenance_ledgers/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
